/* eslint-disable no-lonely-if */
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Cookie from 'js-cookie';
import { nanoid } from 'nanoid';
import Image from 'components/Image';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { ROOTURL } from '../../../config';
import {
  GET_LINKED_ACCOUNTS,
  SET_LINKED_ACCOUNTS,
} from '../../../helpers/graphql/settings';
import graphQLClient from '../../../helpers/graphQLClient';
import HiveLogo from '../../../images/hive.svg';
import SteemLogo from '../../../images/steem.svg';

const steemBtnTheme = createTheme({
  palette: {
    primary: {
      main: '#171fc9',
    },
  },
});

const hiveBtnTheme = createTheme({
  palette: {
    primary: {
      main: '#E31337',
    },
  },
});

const AccountControl = ({ account, isEnabled, setEnabled, isSteem }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [csrfState, setCsrfState] = useState(undefined);

  useEffect(() => {
    const existingCookie = Cookie.get('csrf_state');
    if (existingCookie) {
      setCsrfState(existingCookie);
    } else {
      const state = nanoid();
      Cookie.set('csrf_state', state);
      setCsrfState(state);
    }
  }, []);

  const newNotification = notification => {
    if (notification !== undefined) {
      let variant = 'success';
      if (notification.success === false) {
        variant = 'error';
      }
      enqueueSnackbar(notification.message, { variant });
    }
  };

  const toggleAccount = () => {
    setEnabled(!isEnabled);
    const variables = isSteem
      ? { isSteem: !isEnabled }
      : { isHive: !isEnabled };
    graphQLClient(SET_LINKED_ACCOUNTS, variables).then(
      ({ setLinkedAccounts }) => {
        if (!setLinkedAccounts.success) newNotification(setLinkedAccounts);
      },
    );
  };

  return (
    <>
      <Typography gutterBottom>{account && `Linked to ${account}`}</Typography>
      <div>
        <a
          href={
            isSteem
              ? `https://steemlogin.com/login-request/travelfeed.app?redirect_uri=${
                  ROOTURL === 'https://travelfeed.com'
                    ? 'https://travelfeed.io'
                    : ROOTURL
                }%2Fdashboard%2Fsettings%2Fupdatelinkedaccounts%2Fsteem&scope=posting&state=${csrfState}`
              : `https://hivesigner.com/login-request/travelfeed.app?redirect_uri=${ROOTURL}%2Fdashboard%2Fsettings%2Fupdatelinkedaccounts%2Fhive&scope=posting&state=${csrfState}`
          }
          rel="noopener noreferrer nofollow"
          target="_blank"
        >
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={isSteem ? steemBtnTheme : hiveBtnTheme}>
              <Button color="primary" variant="contained" className="tw-my-4">
                <Image
                  src={isSteem ? SteemLogo : HiveLogo}
                  alt={`Sign up for ${isSteem ? 'Steem' : 'Hive'}`}
                  height={25}
                  width={isSteem ? 25 : 30}
                />
                <span className="ml-2">
                  Link {account ? 'a different' : 'an'} account
                </span>
              </Button>
            </ThemeProvider>
          </StyledEngineProvider>{' '}
        </a>
      </div>
      <FormControl variant="standard" fullWidth>
        <FormGroup>
          <FormControlLabel
            labelPlacement="end"
            control={
              <Switch
                disabled={!account}
                checked={isEnabled}
                onChange={toggleAccount}
                color="primary"
              />
            }
            label="Enable cross-posting"
          />
        </FormGroup>
      </FormControl>
    </>
  );
};

const LinkAccounts = props => {
  const { steem, hive, showIfLinked } = props;

  const [postToHive, setPostToHive] = useState(false);
  const [hiveUser, setHiveUser] = useState(undefined);
  const [postToSteem, setPostToSteem] = useState(false);
  const [steemUser, setSteemUser] = useState(undefined);

  useEffect(() => {
    graphQLClient(GET_LINKED_ACCOUNTS).then(({ preferences }) => {
      setPostToHive(preferences.postToHive);
      setHiveUser(preferences.hiveUser);
      setPostToSteem(preferences.postToSteem);
      setSteemUser(preferences.steemUser);
    });
  }, []);

  return (
    <>
      <FormLabel component="legend">
        If you link your {steem ? 'Steem' : 'Hive'} account and enable
        cross-posting, your actions on TravelFeed (posts, comments, votes) will
        be automatically broadcasted to the {steem ? 'Steem' : 'Hive'}{' '}
        blockchain
        {steem
          ? ''
          : ' and your comments and posts from Hive will be synced to TravelFeed'}
        .
      </FormLabel>
      {hive && (
        <AccountControl
          account={hiveUser}
          isEnabled={postToHive}
          setEnabled={setPostToHive}
        />
      )}
      {hiveUser && showIfLinked}
      {steem && (
        <AccountControl
          account={steemUser}
          isEnabled={postToSteem}
          setEnabled={setPostToSteem}
          isSteem
        />
      )}
    </>
  );
};

export default LinkAccounts;
